import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "note-editor-base" }
const _hoisted_2 = { class: "title-wrapper" }
const _hoisted_3 = { class: "title-icons" }

import { ref, computed, onMounted, onBeforeUnmount, watch, toRef } from 'vue';
import Editor from '@/components/editor/Editor';
import AutoExpandTextarea from '@/components/elements/AutoExpandTextarea';
import useNotes from '@/utilities/composables/useNotes';
import useTranslate from '@/utilities/composables/useTranslate';
import { ResourceType } from '@/types/resources';
import constants from '@/utilities/constants';
import { NoteContentType, noteAiTitlePrompt, NoteTemplate } from '@/types/notes';
import {
  arrowDownInTitle,
  arrowUpInFirstRowOfEditor,
  arrowLeftAtEditorStart,
} from '@/utilities/editorMovement';
import TranscriptionLanguagePicker from '@/components/widgets/TranscriptionLanguagePicker';
import useStreamResponseReader from '@/utilities/composables/useStreamResponseReader';
import EventBus from '@/utilities/eventBus';
import EscTooltip from '@/components/widgets/EscTooltip';
import useNoteContent from '@/utilities/composables/useNoteContent';
import { useConfirmDialog } from '@vueuse/core';
import { Portal } from 'portal-vue';
import ConfirmationModal from '@/components/widgets/ConfirmationModal';
import { MacType } from '@/types/macType';
import usePlatform from '@/utilities/composables/usePlatform';
import { downloadFile } from '@/utilities/downloads';
import { NoteTabId } from '@/types/tabs';
import useIsElectronApp from '@/utilities/composables/useIsElectronApp';
import NoteTranscriptionChat from '@/components/notes/NoteTranscriptionChat';
import capitalizeFirstLetter from '@/utilities/capitalizeFirstLetter';
import AudioTranscriptionControl from '@/components/elements/AudioTranscriptionControl';
import useAudioTranscription from '@/utilities/composables/useAudioTranscription';
import SuggestedActions from '@/components/notes/SuggestedActions';
import { useStore } from 'vuex';
import { useRoute } from 'vue-router';
import useToggleSidebar from '@/utilities/composables/useToggleSidebar';
import { ActionStatus } from '@superthread-com/api';
import { globalState } from '@/utilities/globalState';
import RelatedCards from '@/components/notes/RelatedCards';
import NoteMetadata from '@/components/notes/NoteMetadata';

const MAX_INSTRUCTIONS_LENGTH = 4096;


export default /*@__PURE__*/_defineComponent({
  __name: 'NoteEditor',
  props: {
  noteId: {
    type: String,
    required: true,
  },

  isQuickNote: {
    type: Boolean,
    required: false,
    default: false,
  },
},
  emits: ['createNote', 'close'],
  setup(__props, { emit: __emit }) {

const { isElectronApp } = useIsElectronApp();
const store = useStore();
const route = useRoute();

const aiGeneratedText = ref('');
const noteTitleInput = ref();
const noteEditor = ref();
const tempNoteTitle = ref('');
const tempNoteContent = ref('');
const noteCreating = ref(false);

const emit = __emit;

const { convertStreamResponse } = useStreamResponseReader(aiGeneratedText);
const { translate } = useTranslate();
const {
  createNote,
  updateNoteTitle,
  updateNoteContent,
  clearUpdateNoteDebounceTimeout,
  getAiNote,
  updateLastNoteTemplateId,
  getLastNoteTemplate,
  getNoteHasAiNotes,
} = useNotes();
const { isWindows } = usePlatform();
const { expandSidebar } = useToggleSidebar();

const props = __props;

const noTranscriptAvailableDialog = useConfirmDialog();
const transcriptsDialog = useConfirmDialog();

const {
  noteTemplateId,
  noteContent: _noteContent,
  noteTitle: _noteTitle,
} = useNoteContent(toRef(props, 'noteId'));

const { getTranscriptContents, isRecordingStopped, stopRecording, currentTranscriptionNoteId } =
  useAudioTranscription(toRef(props, 'noteId'));

const notes = computed(() => store.getters.getNotes);

const noteContent = computed(() => tempNoteContent.value || _noteContent.value);

const teamId = computed(() => store.getters.getTeamID);

const resourceId = computed(() => `${teamId.value}-editor-${ResourceType.Note}-${props.noteId}`);

const noteTitle = computed({
  get: () => tempNoteTitle.value || _noteTitle.value || '',
  set: (title) => {
    tempNoteTitle.value = title;
    handleTitleChange();
  },
});

const aiNote = computed(() => getAiNote(props.noteId, noteTemplateId.value));

const aiNoteId = computed(() => aiNote.value?.id);

const aiNoteActions = computed(() => aiNote.value?.actions || []);

const pendingAiNoteActions = computed(() => {
  return aiNoteActions.value.filter((action: any) => action.status === ActionStatus.Pending);
});

const hasNoteTranscripts = computed(
  (): boolean => !!(notes.value[props.noteId]?.transcripts || []).length
);

const aiNoteGenerating = computed(() => store.getters.getAiNoteGenerating);

const copyTranscriptIcon = computed(() => (getTranscriptContents() ? 'clipboard' : ''));

const suggestedActionsAvailable = computed(() => {
  return aiNoteActions.value.length > 0;
});

const suggestedAiActionsFeatureAvailable = computed(() => {
  return store.getters.getTeamFeatureEnabled(constants.featureFlagIds.aiSuggestedActions);
});

const isOnboardingNote = computed(() => notes.value[props.noteId]?.isOnboardingNote);
const displaySuggestedActions = computed(() => {
  return (
    suggestedAiActionsFeatureAvailable.value &&
    !props.isQuickNote &&
    globalState.showSuggestedActions
  );
});

const copyTranscriptToClipboard = async () => {
  await navigator.clipboard.writeText(getTranscriptContents(null, false));

  store.dispatch('addUiNotification', {
    message: translate('transcriptCopiedToClipboard'),
    duration: 4000,
    icon: 'clipboard',
  });
};

const handleTranscriptClick = () => {
  if (!hasNoteTranscripts.value && !isElectronApp.value) {
    noTranscriptAvailableDialog.reveal();
  } else {
    transcriptsDialog.reveal();
  }
};

const handleDownloadMacApp = (type: MacType) => {
  downloadFile(
    type === MacType.MacIntel
      ? constants.downloadLinks.macx64.link
      : constants.downloadLinks.macArm64.link
  );
  noTranscriptAvailableDialog.confirm();
};

const focusEditor = (position: string = 'end') => {
  noteEditor?.value.editor.commands.focus(position);
};

const blurEditor = () => {
  noteEditor?.value.editor.commands.blur();
};

const handleClickNoteEditorWrap = (e) => {
  /**
   * If the click is not on the editor content itself, focus the editor
   * to the end position
   */
  if (e.target?.classList?.contains('__view')) focusEditor('end');
};

const handleArrowDownInTitle = ({ cursorX }) => {
  arrowDownInTitle(noteEditor.value, cursorX, focusEditor);
};

const handleArrowUpInFirstRowEditor = ({ rect }) => {
  arrowUpInFirstRowOfEditor(noteTitleInput.value, { rect });
};

const handleArrowLeftAtEditorStart = () => {
  arrowLeftAtEditorStart(noteTitleInput.value);
};

const createNewNoteFromTranscription = (noteId: string) => {
  if (!noteId) return;
  emit('createNote', noteId);
};

const createNewNote = async (noContent) => {
  const noteId = await createNote({ title: tempNoteTitle.value });
  emit('createNote', noteId);
  noteCreating.value = false;

  if (!noContent) {
    updateNoteContent(noteId, tempNoteContent.value || '');
  } else {
    updateNoteTitle(noteId, tempNoteTitle.value);
  }

  return noteId;
};

const startCreatingNote = async (noContent) => {
  noteCreating.value = true;
  await createNewNote(noContent);
};

const handleTitleChange = async () => {
  if (props.noteId === constants.newNote && !noteCreating.value) {
    await startCreatingNote(true);
    return;
  }

  updateNoteTitle(props.noteId, tempNoteTitle.value, false, () => {
    tempNoteTitle.value = '';
  });
};

const handleContentChange = async (content = '') => {
  tempNoteContent.value = content;

  if (props.noteId === constants.newNote && !noteCreating.value) {
    await startCreatingNote(false);
    return;
  }

  updateNoteContent(props.noteId, content, aiNote.value?.id);
};

const emitClose = () => {
  emit('close');
};

const clearNoteDebounce = () => {
  if (props.noteId === constants.newNote) return;

  clearUpdateNoteDebounceTimeout({
    noteId: props.noteId,
    title: tempNoteTitle.value,
    content: noteContent.value,
    aiNoteId: aiNote.value?.id,
  });
};

const setNoteGenerating = (value, noContent = false) => {
  if (noContent) store.dispatch('setAiNoteGeneratingTitle', value);
  else store.dispatch('setAiNoteGenerating', value);
};

const generateTitleAndContent = async () => {
  const note = notes.value[props.noteId];
  if (!(note?.transcripts || []).length) return;

  if (aiNoteGenerating.value) return;

  if (!note.title) {
    await generateAiTitle();
  }
  if (!getNoteHasAiNotes(props.noteId)) {
    EventBus.$emit('note:switchToTab', NoteTabId.Enhanced);
    EventBus.$emit('note:updateNoteTemplate', NoteTemplate.AutoEnhanced);
    await generateAiNote(NoteTemplate.AutoEnhanced);
  }
};

const generateAiTitle = async () => {
  const noteId = props.noteId;

  if (tempNoteTitle.value !== '' || notes.value[noteId]?.title) return;

  aiGeneratedText.value = '';

  setNoteGenerating(noteId, true);

  const userNotes = notes.value[noteId]?.[NoteContentType.UserNotes] || '';
  const instructions = `${noteAiTitlePrompt} ${userNotes} ${getTranscriptContents()}`.substring(
    0,
    MAX_INSTRUCTIONS_LENGTH
  );

  const response = await store.dispatch('AiTask', {
    generate: { instructions },
  });
  await convertStreamResponse(response, false);

  tempNoteTitle.value = aiGeneratedText.value;
  updateNoteTitle(noteId, tempNoteTitle.value);

  setNoteGenerating('', true);
};

const generateAiNote = async (templateId: string) => {
  if (!templateId || !props.noteId) return;

  // Clear previous generation
  aiGeneratedText.value = '';

  setNoteGenerating(props.noteId);

  noteTemplateId.value = templateId;
  updateLastNoteTemplateId(props.noteId, noteTemplateId.value);

  if (suggestedActionsAvailable.value) {
    // clear previous ai suggested actions
    store.dispatch('setAiActions', {
      noteId: props.noteId,
      aiNoteId: aiNoteId.value,
      actions: [],
    });
  }

  const response = await store.dispatch('applyNoteTemplate', {
    noteId: props.noteId,
    templateId,
  });

  const aiNote = await convertStreamResponse(response, false);

  store.dispatch('setAiNote', {
    noteId: props.noteId,
    templateId,
    aiNote: {
      id: aiNote.id,
      note_template_id: noteTemplateId.value,
      content: aiGeneratedText.value,
    },
  });

  generateAiActions({ aiNoteId: aiNote.id });

  tempNoteContent.value = aiGeneratedText.value;
  updateNoteContent(props.noteId, tempNoteContent.value, aiNote.id);

  setNoteGenerating('');
};

const generateAiActions = ({
  aiNoteId,
  noteId = props.noteId,
}: {
  aiNoteId?: string;
  noteId?: string;
}) => {
  if (!noteId || !suggestedAiActionsFeatureAvailable.value) return;
  const aiNoteIdCheck = aiNoteId || aiNote.value?.id;
  if (!aiNoteIdCheck) return;
  store.dispatch('generateAiActions', {
    noteId,
    aiNoteId: aiNoteIdCheck,
  });
};

const applyTemplateHandler = async ({
  templateId,
  noteId,
  shouldUpdateLastNoteTemplateId = true,
  forceRegenerate,
}: {
  templateId: string;
  noteId: string;
  shouldUpdateLastNoteTemplateId: boolean;
  forceRegenerate: boolean;
}) => {
  if (noteId !== props.noteId) return;

  if (getAiNote(props.noteId, templateId) && !forceRegenerate) {
    noteTemplateId.value = templateId;

    if (shouldUpdateLastNoteTemplateId)
      updateLastNoteTemplateId(props.noteId, noteTemplateId.value);
    return;
  }

  generateAiNote(templateId);
};

const tabChangeHandler = async ({
  noteId,
  templateId,
  shouldUpdateLastNoteTemplateId,
  shouldCreateAiNote,
}: {
  noteId: string;
  templateId: string;
  shouldUpdateLastNoteTemplateId: boolean;
  shouldCreateAiNote: boolean;
}) => {
  if (noteId !== props.noteId) return;

  noteTemplateId.value = templateId;

  if (shouldCreateAiNote) {
    await generateAiNote(templateId);
    return;
  }

  if (shouldUpdateLastNoteTemplateId) updateLastNoteTemplateId(props.noteId, noteTemplateId.value);
};

const resetEditorContent = () => {
  const contentToSet = !noteTemplateId.value
    ? notes.value[props.noteId]?.[NoteContentType.UserNotes]
    : aiNote.value?.content;

  noteEditor.value.editor.commands.setContent(contentToSet);
};

const setupNote = () => {
  applyTemplateHandler({
    templateId: getLastNoteTemplate(props.noteId),
    noteId: props.noteId,
    shouldUpdateLastNoteTemplateId: false,
    forceRegenerate: false,
  });
};

const toggleSuggestedActions = (value: boolean | null | undefined) => {
  globalState.showSuggestedActions = value ?? !globalState.showSuggestedActions;
};

const handleCloseNotesOnboarding = () => {
  if (props.noteId !== currentTranscriptionNoteId.value && currentTranscriptionNoteId.value) return;

  const recordingStopped = isRecordingStopped.value;

  expandSidebar();
  stopRecording();

  if (
    ![constants.routeNames.notes, constants.routeNames.note].includes(route.name) ||
    (!isOnboardingNote.value && !route?.query?.fromTutorial)
  )
    return;

  /**
   * If the transcription has been stopped beforehand, it has already been enhanced,
   * so we don't need to enhance it again.
   */
  if (!recordingStopped) {
    EventBus.$emit('note:switchToTab', NoteTabId.Enhanced);
    tabChangeHandler({
      noteId: props.noteId,
      templateId: NoteTemplate.AutoEnhanced,
      shouldUpdateLastNoteTemplateId: true,
      shouldCreateAiNote: true,
    });
  }
};

watch(aiGeneratedText, (newValue) => {
  if (aiNoteGenerating.value) noteEditor.value?.editor.commands.setContent(newValue);
});

watch(noteTemplateId, () => {
  resetEditorContent();
});

// Clear debounce timer and update if timeout is in progress when switching notes
const noteId = computed(() => props.noteId);
watch(
  noteId,
  (newValue, oldValue) => {
    if (oldValue === constants.newNote) return;

    /**
     * Get previous AI note with correct previous template id used,
     * if it exists use its content to update the note with,
     * otherwise update the regular top-level note content
     */
    const previousNoteTemplateId = notes.value[oldValue]?.last_note_template_id;
    const previousAiNote = getAiNote(oldValue, previousNoteTemplateId);
    const previousNoteContent = previousAiNote
      ? previousAiNote.content
      : notes.value[oldValue]?.[NoteContentType.UserNotes];

    clearUpdateNoteDebounceTimeout({
      noteId: oldValue,
      title: tempNoteTitle.value,
      content: previousNoteContent,
      aiNoteId: previousAiNote?.id,
    });

    tempNoteTitle.value = '';
    tempNoteContent.value = '';
    store.dispatch('setAiNoteGenerating', '');

    noteTemplateId.value = getLastNoteTemplate(newValue);

    focusEditor();
  },
  { deep: true }
);

onMounted(() => {
  setupNote();
  focusEditor();

  EventBus.$on('note:tabChange', tabChangeHandler);
  EventBus.$on('note:applyTemplate', applyTemplateHandler);
  EventBus.$on('note:resetEditorContent', resetEditorContent);
  EventBus.$on('note:toggleSuggestedActions', toggleSuggestedActions);

  if (isElectronApp.value) {
    window.electron.on('on-close-notes-onboarding-channel', async () => {
      handleCloseNotesOnboarding();
    });

    window.electron.on('stop-transcription-channel', () => {
      stopRecording();
    });
  }
});

onBeforeUnmount(() => {
  clearNoteDebounce();

  EventBus.$off('note:tabChange', tabChangeHandler);
  EventBus.$off('note:applyTemplate', applyTemplateHandler);
  EventBus.$off('note:resetEditorContent', resetEditorContent);
  EventBus.$off('note:toggleSuggestedActions', toggleSuggestedActions);

  store.dispatch('setAiNoteGenerating', '');

  if (isElectronApp.value) {
    window.electron.removeAllListeners('on-close-notes-onboarding-channel');
    window.electron.removeAllListeners('stop-transcription-channel');
  }
});

return (_ctx: any,_cache: any) => {
  const _component_vue_scroll = _resolveComponent("vue-scroll")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createVNode(_unref(AutoExpandTextarea), {
        ref_key: "noteTitleInput",
        ref: noteTitleInput,
        value: noteTitle.value,
        class: "note-title-textarea-wrapper pl-1",
        "text-value": noteTitle.value,
        "textarea-class": 'note-title-textarea-input',
        "initial-height": 24,
        "handle-arrow-down-in-last-line": true,
        placeholder: _unref(translate)('untitledNote'),
        onInput: _cache[0] || (_cache[0] = ($event: any) => (noteTitle.value = $event.target.value)),
        onTabpress: _cache[1] || (_cache[1] = ($event: any) => (focusEditor('start'))),
        onEnter: _cache[2] || (_cache[2] = ($event: any) => (focusEditor('start'))),
        onArrowDownRightEnd: _cache[3] || (_cache[3] = ($event: any) => (focusEditor('start'))),
        onArrowDownLastLine: handleArrowDownInTitle,
        onEsc: emitClose
      }, null, 8, ["value", "text-value", "placeholder"]),
      _cache[8] || (_cache[8] = _createTextVNode()),
      _createElementVNode("div", _hoisted_3, [
        _createVNode(_unref(TranscriptionLanguagePicker)),
        _cache[6] || (_cache[6] = _createTextVNode()),
        _createVNode(_unref(AudioTranscriptionControl), {
          "resource-id": props.noteId,
          "is-quick-note": props.isQuickNote,
          onOpenTranscripts: handleTranscriptClick,
          onStartRecording: createNewNoteFromTranscription,
          onStopRecording: generateTitleAndContent
        }, null, 8, ["resource-id", "is-quick-note"]),
        _cache[7] || (_cache[7] = _createTextVNode()),
        (__props.isQuickNote)
          ? (_openBlock(), _createBlock(_unref(EscTooltip), {
              key: 0,
              class: "note-editor-expand",
              onClosePopup: emitClose
            }))
          : _createCommentVNode("", true)
      ])
    ]),
    _cache[9] || (_cache[9] = _createTextVNode()),
    (props.noteId !== _unref(constants).newNote)
      ? (_openBlock(), _createBlock(_unref(NoteMetadata), {
          key: props.noteId,
          class: "ml-4 mb-2.5",
          "note-id": props.noteId
        }, null, 8, ["note-id"]))
      : _createCommentVNode("", true),
    _cache[10] || (_cache[10] = _createTextVNode()),
    _createVNode(_component_vue_scroll, {
      class: "note-editor-scroll",
      ops: {
        scrollPanel: { scrollingX: false },
        rail: { gutterOfSide: '0px' },
        bar: { size: '4px', keepShow: false },
      },
      onClick: handleClickNoteEditorWrap
    }, {
      default: _withCtx(() => [
        _createVNode(_unref(Editor), {
          ref_key: "noteEditor",
          ref: noteEditor,
          "resource-type": 'note',
          "resource-id": resourceId.value,
          content: noteContent.value,
          "watch-content": false,
          "handle-arrow-left": true,
          "bubble-order": {
          headingSection: 0,
          textStyleSection: 1,
          alignTextSection: 2,
          mediaSection: 3,
          listsSection: 4,
          blockElementsSection: 5,
          addCommentButton: 6,
          createCardFromSelection: 7,
          aiSection: 8,
        },
          "highlight-allowed": true,
          "enable-text-alignment": true,
          "enable-ai": true,
          "enable-board-embed": true,
          "enable-placeholder": false,
          onInput: handleContentChange,
          onArrowUpInFirstRow: handleArrowUpInFirstRowEditor,
          onArrowLeftAtDocStart: handleArrowLeftAtEditorStart,
          onEsc: blurEditor
        }, null, 8, ["resource-id", "content"])
      ]),
      _: 1
    }),
    _cache[11] || (_cache[11] = _createTextVNode()),
    (displaySuggestedActions.value)
      ? (_openBlock(), _createBlock(_unref(SuggestedActions), {
          key: 1,
          actions: pendingAiNoteActions.value,
          "ai-note-id": aiNoteId.value
        }, null, 8, ["actions", "ai-note-id"]))
      : _createCommentVNode("", true),
    _cache[12] || (_cache[12] = _createTextVNode()),
    (suggestedAiActionsFeatureAvailable.value)
      ? (_openBlock(), _createBlock(_unref(RelatedCards), {
          key: 2,
          "ai-note-id": aiNoteId.value,
          "template-id": _unref(noteTemplateId),
          "note-id": props.noteId
        }, null, 8, ["ai-note-id", "template-id", "note-id"]))
      : _createCommentVNode("", true),
    _cache[13] || (_cache[13] = _createTextVNode()),
    (_unref(noTranscriptAvailableDialog).isRevealed.value)
      ? (_openBlock(), _createBlock(_unref(Portal), {
          key: 3,
          to: "root-widget"
        }, {
          default: _withCtx(() => [
            _createVNode(_unref(ConfirmationModal), {
              title: _unref(translate)('noTranscriptAvailable'),
              description: _unref(translate)('enhanceNotesUsingMacApp'),
              "confirm-button-text": _unref(translate)(_unref(MacType).MacIntel),
              "confirm-button-icon": 'download',
              "confirm-button-icon-color": 'var(--attentionIcon)',
              "confirm-button-class": "no-transcript-confirm",
              "confirm-button-disabled": _unref(isWindows),
              "additional-confirm-button-text": _unref(translate)(_unref(MacType).AppleSilicon),
              "additional-confirm-button-icon": 'download',
              "additional-confirm-button-icon-color": 'var(--attentionIcon)',
              onConfirm: _cache[4] || (_cache[4] = ($event: any) => (handleDownloadMacApp(_unref(MacType).MacIntel))),
              onAdditionalConfirm: _cache[5] || (_cache[5] = ($event: any) => (handleDownloadMacApp(_unref(MacType).AppleSilicon))),
              onCancel: _unref(noTranscriptAvailableDialog).cancel
            }, null, 8, ["title", "description", "confirm-button-text", "confirm-button-disabled", "additional-confirm-button-text", "onCancel"])
          ]),
          _: 1
        }))
      : _createCommentVNode("", true),
    _cache[14] || (_cache[14] = _createTextVNode()),
    (_unref(transcriptsDialog).isRevealed.value)
      ? (_openBlock(), _createBlock(_unref(Portal), {
          key: 4,
          disabled: __props.isQuickNote,
          to: "root-widget"
        }, {
          default: _withCtx(() => [
            _createVNode(_unref(ConfirmationModal), {
              title: _unref(capitalizeFirstLetter)(_unref(translate)('transcript')),
              "width-class": 'flex flex-col overflow-hidden h-3/4 w-3/5 max-w-[600px]',
              "show-confirm-buttons": false,
              "header-button-icon": copyTranscriptIcon.value,
              "header-button-tooltip": _unref(translate)('copyTranscriptToClipboard'),
              onHeaderButtonClick: copyTranscriptToClipboard,
              onCancel: _unref(transcriptsDialog).cancel
            }, {
              content: _withCtx(() => [
                _createVNode(_unref(NoteTranscriptionChat), {
                  "note-id": props.noteId
                }, null, 8, ["note-id"])
              ]),
              _: 1
            }, 8, ["title", "header-button-icon", "header-button-tooltip", "onCancel"])
          ]),
          _: 1
        }, 8, ["disabled"]))
      : _createCommentVNode("", true)
  ]))
}
}

})